import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChatEnhanced } from "@/hooks/useChatEnhanced";
import { toast } from "@/hooks/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import ChatRibbons, { RibbonType } from "@/components/chat/ChatRibbons";
import AddressRibbon from "@/components/chat/AddressRibbon";
import MediaRibbon from "@/components/chat/MediaRibbon";
import ChatMessageList from "@/components/chat/ChatMessageList";
import ChatInput from "@/components/chat/ChatInput";
import { ChatBlockingStatus } from "@/components/chat/ChatBlockingStatus";
import AdPanel from "@/components/ads/AdPanel";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import { ArrowLeft, Car, Wifi, WifiOff } from "lucide-react";
import { profileRouterService } from "@/services/profileRouterService";
interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}
interface MatchUser {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  photo_urls: string[] | null;
}
const ChatConversation = () => {
  const {
    matchId
  } = useParams<{
    matchId: string;
  }>();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const {
    messages,
    metrics,
    loading,
    sending,
    connectionStatus,
    partnerTyping,
    partnerOnline,
    sendMessage,
    retryMessage,
    handleTyping
  } = useChatEnhanced(matchId);
  const [reactions, setReactions] = useState<{
    [messageId: string]: MessageReaction[];
  }>({});
  const [otherUser, setOtherUser] = useState<MatchUser | null>(null);
  const [activeRibbon, setActiveRibbon] = useState<RibbonType>("messages");
  const [isBlocked, setIsBlocked] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const parseMatchId = (matchId: string): string[] => {
    if (matchId.length < 73) return [];
    const firstUuidEnd = 36;
    const user1Id = matchId.substring(0, firstUuidEnd);
    const user2Id = matchId.substring(firstUuidEnd + 1);
    return [user1Id, user2Id];
  };
  useEffect(() => {
    if (user && matchId) {
      loadChatData();
    }
  }, [user, matchId]);
  useEffect(() => {
    if (activeRibbon === "messages") {
      scrollToBottom();
    }
  }, [messages, activeRibbon]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth"
    });
  };
  const loadChatData = async () => {
    if (!user || !matchId) return;
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Chat data load timeout')), 10000); // 10 second timeout
    });
    
    try {
      console.log('📨 [ChatConversation] Loading chat data for match:', matchId);
      
      const userIds = parseMatchId(matchId);
      const otherUserId = userIds.find(id => id !== user.id);
      
      if (!otherUserId) {
        console.error('❌ [ChatConversation] Could not parse other user ID from matchId:', matchId);
        toast({
          title: "Error",
          description: "Invalid conversation ID",
          variant: "destructive"
        });
        return;
      }

      console.log('📨 [ChatConversation] Loading profile for user:', otherUserId);
      
      // Load profile with timeout
      const profilePromise = supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name, display_name, photo_urls')
        .eq('user_id', otherUserId)
        .maybeSingle();

      const { data: profile, error: profileError } = await Promise.race([
        profilePromise,
        timeoutPromise
      ]) as any;

      if (profileError) {
        console.warn('⚠️ [ChatConversation] Error loading profile:', profileError);
        // Don't fail completely, just set a fallback user
        setOtherUser({
          user_id: otherUserId,
          first_name: null,
          last_name: null,
          display_name: 'Unknown User',
          photo_urls: null
        });
      } else if (profile) {
        console.log('✅ [ChatConversation] Profile loaded successfully');
        setOtherUser(profile);
      } else {
        console.warn('⚠️ [ChatConversation] No profile found, using fallback');
        setOtherUser({
          user_id: otherUserId,
          first_name: null,
          last_name: null,
          display_name: 'Unknown User',
          photo_urls: null
        });
      }
      
      // Load reactions but don't block if it fails
      try {
        await Promise.race([loadReactions(), timeoutPromise]);
      } catch (reactionError) {
        console.warn('⚠️ [ChatConversation] Failed to load reactions:', reactionError);
        // Continue without reactions
      }
      
    } catch (error: any) {
      console.error('❌ [ChatConversation] Error loading chat data:', error);
      
      if (error.message === 'Chat data load timeout') {
        toast({
          title: "Loading Timeout",
          description: "Chat is taking longer than expected to load",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to load conversation details",
          variant: "destructive"
        });
      }
      
      // Set fallback user so chat isn't completely broken
      const userIds = parseMatchId(matchId || '');
      const otherUserId = userIds.find(id => id !== user.id);
      if (otherUserId) {
        setOtherUser({
          user_id: otherUserId,
          first_name: null,
          last_name: null,
          display_name: 'Unknown User',
          photo_urls: null
        });
      }
    }
  };
  const loadReactions = async () => {
    if (!user || !matchId) return;
    const userIds = parseMatchId(matchId);
    const otherUserId = userIds.find(id => id !== user.id);
    if (!otherUserId) return;
    try {
      const {
        data: reactionsData
      } = await supabase.from('message_reactions').select(`
          *,
          messages!inner(*)
        `).or(`messages.sender_id.eq.${user.id},messages.receiver_id.eq.${user.id}`);
      if (reactionsData) {
        const groupedReactions: {
          [messageId: string]: MessageReaction[];
        } = {};
        reactionsData.forEach((reaction: any) => {
          if (!groupedReactions[reaction.message_id]) {
            groupedReactions[reaction.message_id] = [];
          }
          groupedReactions[reaction.message_id].push(reaction);
        });
        setReactions(groupedReactions);
      }
    } catch (error) {
      console.error('Error loading reactions:', error);
    }
  };
  const handleReaction = async (messageId: string, emoji: string) => {
    if (!user) return;
    try {
      const existingReaction = reactions[messageId]?.find(r => r.user_id === user.id && r.emoji === emoji);
      if (existingReaction) {
        await supabase.from('message_reactions').delete().eq('id', existingReaction.id);
      } else {
        await supabase.from('message_reactions').insert({
          message_id: messageId,
          user_id: user.id,
          emoji: emoji
        });
      }
      await loadReactions();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };
  const handleSendMessage = async (content: string) => {
    const success = await sendMessage(content);
    return success;
  };

  const handleSendVoiceMessage = async (audioBlob: Blob, transcription?: string) => {
    // Upload audio to storage
    try {
      const fileName = `voice_${Date.now()}.webm`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('alpha')
        .upload(`voice-messages/${fileName}`, audioBlob, {
          contentType: 'audio/webm'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('alpha')
        .getPublicUrl(uploadData.path);

      // Send voice message with transcription
      const messageContent = transcription || "🎤 Voice message";
      const success = await sendMessage(messageContent, {
        message_type: 'voice',
        file_url: publicUrl,
        file_type: 'audio/webm',
        file_size: audioBlob.size,
        duration: Math.floor(audioBlob.size / 1000) // Rough estimate
      });

      return success;
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast({
        title: "Error",
        description: "Failed to send voice message",
        variant: "destructive"
      });
      return false;
    }
  };
  const handleProfileClick = () => {
    if (otherUser?.user_id) {
      navigate(profileRouterService.getProfileRoute(otherUser.user_id, otherUser.display_name));
    }
  };
  const getPartnerDisplayName = () => {
    if (!otherUser) return "Unknown User";
    if (otherUser.display_name) {
      return otherUser.display_name;
    }
    const fullName = `${otherUser.first_name || ""} ${otherUser.last_name || ""}`.trim();
    return fullName || "Unknown User";
  };
  const getPartnerAvatar = () => {
    if (otherUser?.photo_urls && otherUser.photo_urls.length > 0) {
      return otherUser.photo_urls[0];
    }
    return null;
  };
  const renderRibbonContent = () => {
    switch (activeRibbon) {
      case "address":
        return <AddressRibbon otherUser={otherUser} />;
      case "media":
        return <MediaRibbon />;
      case "messages":
      default:
        return (
          <div className="p-4">
            {otherUser && (
              <ChatBlockingStatus 
                partnerId={otherUser.user_id}
                onBlockStatusChange={setIsBlocked}
              />
            )}
            <ChatMessageList 
              messages={messages} 
              reactions={reactions} 
              onReaction={handleReaction} 
              onRetry={retryMessage} 
              partnerTyping={partnerTyping} 
              otherUser={otherUser} 
              currentUserId={user?.id} 
              messagesEndRef={messagesEndRef} 
            />
          </div>
        );
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlphaLoader 
            size="xxlarge" 
            className="text-foreground"
          />
          <p className="text-muted-foreground">Loading conversation...</p>
          <Button 
            onClick={() => navigate('/messages')} 
            variant="outline"
            className="mt-4"
          >
            Back to Messages
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-background">
      {/* Advertisement Banner */}
      <AdPanel />
      
      {/* Right Side Menu */}
      <RightSideMenu />
      
      <div className="max-w-md mx-auto flex flex-col min-h-screen pt-[calc(100vh/7)]">
        {/* Sticky Header and Ribbons Container */}
        <div className="flex-shrink-0 sticky top-[calc(100vh/7)] z-40">
          {/* Enhanced Header with Avatar */}
            <div className="bg-card border-b border-border shadow-lg">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center space-x-3">
                  <Link to="/messages" className="text-primary hover:text-primary/80 transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                  </Link>
                  
                  {/* Emergency force exit button when loading is stuck */}
                  {loading && (
                    <Button 
                      onClick={() => navigate('/messages')} 
                      variant="destructive" 
                      size="sm"
                      className="ml-2"
                    >
                      Force Exit
                    </Button>
                  )}
                
                {/* Partner Avatar - Now clickable */}
                <button onClick={handleProfileClick} className="hover:opacity-80 transition-opacity">
                  <Avatar className="w-12 h-12 border-2 border-primary/30">
                    <AvatarImage src={getPartnerAvatar() || undefined} alt={`${getPartnerDisplayName()}'s profile`} />
                    <AvatarFallback className="bg-secondary text-foreground font-semibold">
                      {getPartnerDisplayName()[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
                
                 <div>
                  <button onClick={handleProfileClick} className="font-semibold text-foreground text-lg hover:text-primary transition-colors">
                    {getPartnerDisplayName()}
                  </button>
                  <div className="flex items-center space-x-2">
                    {partnerOnline ? <span className="text-xs text-green-500 flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                        Online
                      </span> : <span className="text-xs text-muted-foreground">Offline</span>}
                  </div>
                </div>
              </div>
              
            </div>
          </div>

          {/* Ribbons */}
          <div className="bg-card border-b border-border">
            <ChatRibbons activeRibbon={activeRibbon} onRibbonChange={setActiveRibbon} />
          </div>
        </div>

        {/* Message Content Area */}
        <div className="flex-1 overflow-auto bg-background">
          {renderRibbonContent()}
        </div>

        {/* Chat Input (only for messages ribbon and when not blocked) */}
        {activeRibbon === "messages" && !isBlocked && <div className="flex-shrink-0">
            <ChatInput 
              onSendMessage={handleSendMessage} 
              onSendVoiceMessage={handleSendVoiceMessage}
              onTyping={handleTyping} 
              sending={sending} 
              connectionStatus={connectionStatus} 
            />
          </div>}

      </div>
    </div>;
};
export default ChatConversation;